import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "the-files-library",
  courseId: "forms",
  order: 6,
  title: "Files library",
  subtitle: "And drafting from files",
  summary:
    "**Files** holds every recording and document you upload, whether or not it belongs to a case. Store, transcribe, and reuse them, or select files and start a report from them.",
  learn: [
    "Add audio and documents to the library, and filter by type and source",
    "Transcribe a file on its own and see where it is used",
    "Select files and create a report in one step",
    "Know how documents are used in the draft",
  ],
  minutes: 5,
  releasedAt: "2026-10-01T00:00:00.000Z",
  body: [
    {
      type: "paragraph",
      text: "**Files** in the sidebar is a library, not a case. Put recordings there when you are not ready to write, stash a PDF you will need later, or keep a reference document the AI can read into a future report. A case also does not have to start with an upload: anything already in your library, from a dictation you saved last week to a scanned statement, can be the source material for a new report.",
    },
    { type: "heading", text: "Store and transcribe" },
    {
      type: "steps",
      items: [
        "Click **Files**, then **Add files**. The modal is titled \"Add to Files\" and accepts audio plus PDF, Word, images, Excel, and CSV.",
        "Set each row to **Narrative**, **Interaction**, or **Other**, add context, and click **Save to Files**.",
        "Use the filters (All types, All audio types, All sources) and \"Search files...\" to find things later.",
        "Open a file for its detail. You can rename it, read its Context, and see \"Used in\" for the cases and forms that reference it.",
        "Click **Transcribe this file** to get a transcript without a case, and choose \"Narrative (your dictation)\" or \"Interaction (an interview)\" when it asks.",
      ],
    },
    {
      type: "callout",
      tone: "tip",
      text: "From the Record Audio modal you can choose **Save to Files** instead of uploading to a case. It asks for a name and a type. That suits a scene note you will fold into the report later.",
    },
    {
      type: "callout",
      tone: "note",
      text: "Deleting a file that a case uses asks you to confirm with **Delete anyway**. The case keeps its narrative but loses the playable audio.",
    },
    { type: "heading", text: "Draft a report from files" },
    {
      type: "steps",
      items: [
        "Open **Files** and tick the files for this report: your dictation, an interview, a CAD printout, a photo of a written statement.",
        "Click **Create report**. The upload modal opens with those files as rows.",
        "Check the audio type on each audio row.",
        "Add context to each document (for example \"CAD log for the incident\").",
        "Set **Report Settings** and click **Upload (N files)**.",
      ],
    },
    {
      type: "paragraph",
      text: "To start from a single file, open its detail page and click **Create report** there.",
    },
    {
      type: "callout",
      tone: "note",
      title: "What happens to documents",
      text: "PDFs, Word files, images, and spreadsheets are extracted to text (OCR for scans and photos) and given to the AI as source material next to the transcript. They are not pasted into the report. The AI draws facts from them.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "A recording you already used stays in Files, so the same interview can feed a second report (a supplemental, a different officer's report) without uploading again. **Previous Uploads** in the upload modal is the same library from the other direction.",
    },
  ],
  related: ["redact-a-pdf", "first-narrative-report", "combine-narrative-and-interaction"],
};

export default lesson;
